/**
 * THEME JSON CONFIG CSS VARS
 * The following default component scss variables can be overwritten via theme configuration per brand.
 * If you need to add new one, please make sure to set a default value.
 * JSON Definition for Component with name "Example": testCssVar: "20px"
 * Resulting CSS var: $example-test-css-var: 20px;
 */

$link-font-size: 18px;
$link-font-size-small: 14px;
$link-line-height: 24px;
$link-line-height-small: 18px;
$link-min-height: 32px;
$link-min-height-small: auto;
$link-font-weight: $font-weight-bold;
$link-margin-icon-text-autosize: 8px;
$link-margin-icon-text: 4px;
$link-border-bottom-width: 2px;

/* END THEME JSON CONFIG CSS VARS ******************************************************* */

:host {
  position: relative;
  margin: 0px;
  padding: 0;
  display: inline-flex;
  --fontWeight: #{$link-font-weight};
  --fontSize: #{$link-font-size};
  --lineHeight: #{$link-line-height};
  --borderBottomWidth: #{$link-border-bottom-width};
  --minHeight: #{$link-min-height};

  .#{$prefix}link-info-icon {
    margin: 4px 16px 0;
  }
}

:host([size="small"]) {
  --fontWeight: #{$link-font-weight};
  --fontSize: #{$link-font-size-small};
  --lineHeight: #{$link-line-height-small};
  --minHeight: #{$link-min-height-small};

  .#{$prefix}link-info-icon {
    margin-top: 0;
  }
}

:host(.#{$prefix}inline) {
  display: inline;

  a {
    display: inherit;

    .#{$prefix}textWrapper {
      display: inherit;
    }
  }
}

:host([icon-position="right"]) {
  a {
    &.#{$prefix}autosize {
      .#{$prefix}textWrapper + .#{$prefix}iconWrapper {
        margin-left: $link-margin-icon-text-autosize;
      }
    }

    .#{$prefix}textWrapper + .#{$prefix}iconWrapper {
      margin-left: $link-margin-icon-text;
    }
  }
}

:host([icon-position="left"]) {
  a {
    &.#{$prefix}autosize {
      .#{$prefix}iconWrapper + .#{$prefix}textWrapper {
        margin-left: $link-margin-icon-text-autosize;
      }
    }

    .#{$prefix}iconWrapper + .#{$prefix}textWrapper {
      margin-left: $link-margin-icon-text;
    }
  }
}

a {
  font-family: $font-family-base;
  font-weight: var(--fontWeight);
  font-size: var(--fontSize);
  line-height: var(--lineHeight);
  display: inline-flex;
  align-items: center;
  transition: color $transition-duration ease-in-out;
  text-decoration: none;
  color: v(contentColor);
  min-height: var(--minHeight);
  cursor: pointer;
  max-width: 100%;

  .#{$prefix}iconWrapper {
    align-self: flex-start;

    eon-ui-icon {
      display: table;
      transition: fill $transition-duration ease-in-out;
      fill: v(contentColor);
      /* Why line-height: 1px?
       * We must remove the line-height from the <a> tag because the height expands the icon-div from 32px to 36px.
       * That will destroy the focus-state (outline-border) if the link is not centered.
      */
      line-height: 1px;
    }
  }

  .#{$prefix}textWrapper {
    align-self: flex-start;
    margin-top: calc(32px * 0.5 - 24px * 0.5);
    padding-bottom: var(--borderBottomWidth);
    overflow: hidden;
    text-overflow: ellipsis;
  }

  span.#{$prefix}text {
    border-bottom: solid v(borderBottomWidth) transparent;
    transition: border $transition-duration ease-in-out;
  }

  &.#{$prefix}size-small {
    span.#{$prefix}text {
      border-bottom: solid 1px transparent;
    }
  }

  &.#{$prefix}hover,
  &:hover {
    text-decoration: none;
    color: v(contentHoverColor);
    border-bottom-color: v(borderBottomWidth) solid v(contentHoverColor);

    .#{$prefix}iconWrapper {
      .#{$prefix}icon {
        fill: v(contentHoverColor);
      }
    }

    span.#{$prefix}text {
      border-bottom: solid v(contentHoverColor);
      border-bottom: v(borderBottomWidth) solid v(contentHoverColor);
    }

    &.#{$prefix}size-small {
      border-bottom-color: 1px solid v(contentHoverColor);
      span.#{$prefix}text {
        border-bottom: solid 1px v(contentHoverColor);
      }
    }
  }

  &:focus {
    outline: none;
    border-bottom-color: v(borderBottomWidth) solid v(contentFocusColor);
    .#{$prefix}iconWrapper {
      .#{$prefix}icon {
        fill: v(contentFocusColor);
      }
    }

    span.#{$prefix}text {
      color: v(contentFocusColor);
      border-bottom: solid v(contentFocusColor);
      border-bottom: v(borderBottomWidth) solid v(contentFocusColor);
    }

    &.#{$prefix}size-small {
      border-bottom-color: 1px solid v(contentFocusColor);
      span.#{$prefix}text {
        color: v(contentFocusColor);
        border-bottom: solid 1px v(contentFocusColor);
      }
    }
  }

  &:focus-within {
    outline: none;
    border-bottom-color: v(borderBottomWidth) solid v(contentFocusColor);
    .#{$prefix}iconWrapper {
      .#{$prefix}icon {
        fill: v(contentFocusColor);
      }
    }

    span.#{$prefix}text {
      color: v(contentFocusColor);
      border-bottom: solid v(contentFocusColor);
      border-bottom: v(borderBottomWidth) solid v(contentFocusColor);
    }

    &.#{$prefix}size-small {
      color: v(contentFocusColor);
      border-bottom-color: 1px solid v(contentFocusColor);
      span.#{$prefix}text {
        border-bottom: solid 1px v(contentFocusColor);
      }
    }
  }

  /* &:focus {
    @include focusBorder(v(contentFocusColor));
    &.#{$prefix}size-small {
      outline: 1px solid;
      outline-offset: 4px;
      outline-color: v(contentFocusColor);
    }
  } */

  /* Do not combine with pseudo-classes above!!!
   * https://medium.com/@jonathanabrams/be-wary-of-the-css-comma-selector-and-pseudo-classes-688c02320e7f
   */
  /* &:focus-within {
    @include focusBorder(v(contentFocusColor));
    &.#{$prefix}size-small {
      outline: 1px solid;
      outline-offset: 4px;
      outline-color: v(contentFocusColor);
    }
  } */

  &.#{$prefix}visibility-always,
  &.#{$prefix}visibility-onHover {
    cursor: default;
  }

  &.#{$prefix}visibility-onClick {
    cursor: pointer;
  }

  &.#{$prefix}disabled {
    pointer-events: none;
    color: v(contentDisableColor);
    transition: unset;

    .#{$prefix}iconWrapper {
      align-self: flex-start;

      eon-ui-icon {
        fill: v(contentDisableColor);
      }
    }

    span.#{$prefix}text {
      color: v(contentDisableColor);
      border-bottom: unset;
      transition: unset;
    }

    &:focus {
      outline: none;
      pointer-events: none;
      color: v(contentDisableColor);
      border-bottom: unset;
      .#{$prefix}iconWrapper {
        .#{$prefix}icon {
          fill: v(contentDisableColor);
        }
      }
      span.#{$prefix}text {
        color: v(contentDisableColor);
        border-bottom: unset;
        transition: unset;
      }

      &.#{$prefix}size-small {
        color: v(contentDisableColor);
        border-bottom: unset;
        span.#{$prefix}text {
          border-bottom: unset;
          transition: unset;
        }
      }
    }

    &:focus-within {
      outline: none;
      pointer-events: none;
      color: v(contentDisableColor);
      border-bottom: unset;
      .#{$prefix}iconWrapper {
        .#{$prefix}icon {
          fill: v(contentDisableColor);
        }
      }
      span.#{$prefix}text {
        color: v(contentDisableColor);
        border-bottom: unset;
        transition: unset;
      }

      &.#{$prefix}size-small {
        color: v(contentDisableColor);
        border-bottom: unset;
        span.#{$prefix}text {
          border-bottom: unset;
          transition: unset;
        }
      }
    }

    &.#{$prefix}hover,
    &:hover {
      pointer-events: none;
      color: v(contentDisableColor);
      .#{$prefix}iconWrapper {
        .#{$prefix}icon {
          fill: v(contentDisableColor);
        }
      }

      span.#{$prefix}text {
        border-bottom: unset;
      }

      &.#{$prefix}size-small {
        border-bottom: unset;
        span.#{$prefix}text {
          border-bottom: unset;
        }
      }
    }
  }
}
